import { Component } from "@angular/core";
import { AuthService } from "app/auth/auth.service";
import { IUser } from "app/auth/user.model";
import { DownloadClickEvent } from "components/download-button/download-button.component";
import { NavParams } from "ionic-angular";
import { IonicPage } from "ionic-angular/navigation/ionic-page";
import { BasePage } from "pages/base-page";
import { ReferralsServiceProvider } from "providers/patient-charts/referrals-service";
import { ReportExecutionService } from "providers/report-execution-service/report-execution.service";
import { StateManagementService } from "providers/state-management/state-management.service";
import { Subscription } from "rxjs";

export interface DischargeSummaryReportPayload {
  format: string;
  parameters: {
    pOrgID: string;
    pPatientID: string;
  };
}

const REPORTS = {
  DISCHARGE_SUMMARY: {
    DOWNLOAD_REPORT_NAME: "Discharge_Summary",
    DOWNLOAD_REPORT_FORMAT: "pdf",
    BUILD_REPORT_NAME: "discharge_summary_pdf",
    BUILD_REPORT_FORMAT: "pdf",
    MEDIA_TYPE: "application/pdf",
  },
} as const;

@IonicPage({
  name: "patient-reports",
  segment: "patient/:id/patient-reports",
})
@Component({
  selector: "patient-reports",
  templateUrl: "patient-reports.html",
})
export class PatientReportsPage extends BasePage {
  private patientChartId: number;
  private patientCareType: String = "";
  private patientCareTypeSubscription: Subscription;
  private user: IUser;

  /**
   * Initializes the patient reports page with required services and sets up
   * initial state.
   * @param referralsService Service for managing patient referrals
   * @param state Service for managing application state
   * @param navParams Navigation parameters containing patient chart ID
   * @param authService Service for authentication and user information
   * @param reportExecutionService Service for executing and downloading reports
   */
  constructor(
    private referralsService: ReferralsServiceProvider,
    private state: StateManagementService,
    private navParams: NavParams,
    private authService: AuthService,
    private reportExecutionService: ReportExecutionService
  ) {
    super(state);
    this.state.setSectionId("Reports");
    this.state.setAssessmentType("Active Patients");
    this.referralsService.ACTIVE_TAB_NAME = "patient-reports";
    this.patientChartId = this.navParams.data.id;
    this.patientCareType = this.state.patientCareType;
  }

  /**
   * Lifecycle hook that initializes component by subscribing to patient care
   * type changes and loading user data.
   */
  ngOnInit() {
    this.patientCareTypeSubscription = this.state
      .getPatientCareTypeObserver()
      .subscribe((careType: String) => {
        this.patientCareType = careType;
      });
    this.user = this.authService.getUser();
  }

  /**
   * Lifecycle hook that cleans up subscriptions when component is destroyed.
   * Unsubscribes this page's own patient-care-type subscription AND delegates to
   * BasePage.ngOnDestroy() so the feature-flag subscriptions the base wires up in
   * its constructor are torn down too — without the super call they leak on every
   * teardown of this page.
   */
  ngOnDestroy() {
    if (this.patientCareTypeSubscription) {
      this.patientCareTypeSubscription.unsubscribe();
    }
    super.ngOnDestroy();
  }

  /**
   * Handles download button click events and routes to appropriate report
   * download handler.
   * @param event The download click event containing the report type to
   * download
   */
  handleDownload(event: DownloadClickEvent): void {
    console.log(`Download requested for: ${event.reportType}`);
    switch (event.reportType) {
      case "discharge-summary":
        this.downloadDischargeSummary();
        break;
      default:
        console.warn(
          `No download handler for report type: ${event.reportType}`
        );
    }
  }

  /**
   * Downloads the discharge summary report for the current patient.
   * Constructs the payload with organization and patient chart IDs and
   * initiates the download.
   */
  private downloadDischargeSummary(): void {
    let payloadDischargeSummary: DischargeSummaryReportPayload = {
      format: REPORTS.DISCHARGE_SUMMARY.DOWNLOAD_REPORT_FORMAT,
      parameters: {
        pOrgID: this.user.tenantId.toString(),
        pPatientID: this.patientChartId.toString(),
      },
    };

    this.reportExecutionService.downloadReport(
      REPORTS.DISCHARGE_SUMMARY.DOWNLOAD_REPORT_NAME,
      payloadDischargeSummary,
      this.reportExecutionService.buildReportFilename(
        REPORTS.DISCHARGE_SUMMARY.BUILD_REPORT_NAME,
        REPORTS.DISCHARGE_SUMMARY.BUILD_REPORT_FORMAT
      ),
      REPORTS.DISCHARGE_SUMMARY.MEDIA_TYPE
    );
  }
}



// WEBPACK FOOTER //
// ./src/pages/patient-chart/patient-reports/patient-reports.ts